import * as React from 'react'
import calendar from '../../assets/calendar.png'
import shield from '../../assets/shield.png'
import { Button } from '../../shared-components/Button'
import { useScreenTracking } from '../../shared-hooks/useScreenTracking'
import { track } from '../../utils/analytics'
import { SignupCard } from './SignupCard'
import { SignupStep } from './SignupFlow'

type SelectCoverageProps = {
  setSignupStep: (step: SignupStep) => void
}

enum CoverageType {
  AlwaysOn = 'AlwaysOn',
  Scheduled = 'Scheduled',
}

export const SelectCoverage = ({
  setSignupStep,
}: SelectCoverageProps): JSX.Element => {
  useScreenTracking('SelectCoverageView', 'View select coverage screen')

  const [coverageType, setCoverageType] = React.useState<
    CoverageType | undefined
  >(undefined)

  const handleContinue = () => {
    if (coverageType === CoverageType.AlwaysOn) {
      track('AlwaysOnCoverageSelection', 'Always on coverage selected')
      setSignupStep(SignupStep.AlwaysOnSummary)
    } else if (coverageType === CoverageType.Scheduled) {
      track('ScheduledCoverageSelection', 'Scheduled coverage selected')
      setSignupStep(SignupStep.SessionPickerSegue)
    }
  }

  return (
    <SignupCard heading='Select Coverage'>
      <div className='flex flex-col gap-4'>
        <CoverageButton
          iconSrc={shield}
          coverageMode='Flexible protection'
          infoStr='Just drive and maintain your acceptance rate. We will automatically add all the hours you qualify for.'
          isPopular
          isSelected={coverageType === CoverageType.AlwaysOn}
          onClick={() => setCoverageType(CoverageType.AlwaysOn)}
        />
        <CoverageButton
          iconSrc={calendar}
          coverageMode='Scheduled protection'
          infoStr="Schedule your protection. We'll only cover times that you drive during the scheduled times."
          isSelected={coverageType === CoverageType.Scheduled}
          onClick={() => setCoverageType(CoverageType.Scheduled)}
        />
        <Button type='button' disabled={!coverageType} onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </SignupCard>
  )
}

interface CoverageButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  iconSrc: string
  coverageMode: string
  infoStr: string
  isSelected?: boolean
  isPopular?: boolean
}

const CoverageButton = ({
  iconSrc,
  coverageMode,
  infoStr,
  isSelected = false,
  isPopular = false,
  ...buttonProps
}: CoverageButtonProps) => (
  <button
    className={`relative rounded-2xl border-4 ${
      isPopular ? 'border-brand-light-purple pt-7' : 'border-[#C1CBDE]'
    } p-6 flex gap-4 ${
      isSelected
        ? 'bg-brand-light-purple bg-opacity-40'
        : 'hover-hover:hover:bg-brand-light-purple hover-hover:hover:bg-opacity-20 active:bg-brand-light-purple active:bg-opacity-20'
    }`}
    {...buttonProps}
  >
    {isPopular && (
      <p className='absolute text-xs font-semibold right-3 -top-4 text-white bg-brand-light-purple px-7 py-1.5 rounded-[.6rem]'>
        POPULAR
      </p>
    )}
    <img className='flex-none mt-0.5' width={60} src={iconSrc} />
    <div className='text-left'>
      <p className='text-xl font-normal mb-2'>{coverageMode}</p>
      <p className='text-sm leading-6 font-extralight text-body-black'>
        {infoStr}
      </p>
    </div>
  </button>
)
